// Sent and waiting (Automations → Sent and waiting): the WhatsApp messages automations sent and the ones they will
// send, a page at a time from the server. "By customer" groups the page's messages per automation run (stop one
// customer's waiting messages); "All messages" is the plain list. Filters: customer, automation, status.
import React, { useEffect, useMemo, useState } from 'react';
import toast from 'react-hot-toast';
import api, { formatDateTime, formatPhone, SALON_NAME } from '../services/api';
import { ChipRow, EmptyState, Loader, Modal, Spinner } from '../components/ui';

const PAGE_SIZE = 50;

const STATUS_COLOR = {
  active: '#2e7d32',
  cancelled: '#c62828',
  completed: '#5c3d99',
  pending: '#b7791f',
  scheduled: '#b7791f',
  sent: '#2e7d32',
  delivered: '#3b6fd1',
  read: '#0f8f6a',
  failed: '#c62828',
};
const STATUS_LABEL = {
  active: 'Running',
  cancelled: 'Stopped',
  completed: 'Done',
  pending: 'Waiting to send',
  scheduled: 'Waiting to send',
  sent: 'Sent',
  delivered: 'Delivered',
  read: 'Read',
  failed: 'Failed',
};
const DONE_STATUSES = ['sent', 'delivered', 'read'];

function SentAs({ value }) {
  if (!value) return <span className="de-muted">—</span>;
  const text = value === 'text';
  return (
    <span
      className={`de-pill de-pill--sm de-pill--square${text ? ' de-pill--info' : ''}`}
      title={text ? 'The customer wrote in the last 24 hours, so plain text was sent' : 'Approved WhatsApp template'}
    >
      {text ? 'Text (24h window)' : 'Template'}
    </span>
  );
}

function Badge({ status, small }) {
  const c = STATUS_COLOR[status] || '#777';
  return (
    <span
      style={{
        display: 'inline-flex',
        alignItems: 'center',
        gap: 4,
        padding: small ? '2px 8px' : '3px 10px',
        borderRadius: 999,
        fontSize: small ? 10 : 11,
        fontWeight: 700,
        background: `${c}18`,
        color: c,
        border: `1px solid ${c}33`,
        whiteSpace: 'nowrap',
      }}
    >
      <span style={{ width: 5, height: 5, borderRadius: '50%', background: c }} />
      {STATUS_LABEL[status] || status}
    </span>
  );
}

function messageLabel(job) {
  return job.label || job.step_key || job.kind;
}

function groupStatus(steps) {
  if (steps.some((s) => s.status === 'pending' || s.status === 'scheduled')) return 'active';
  if (steps.some((s) => DONE_STATUSES.includes(s.status))) return 'completed';
  if (steps.some((s) => s.status === 'cancelled')) return 'cancelled';
  if (steps.some((s) => s.status === 'failed')) return 'failed';
  return 'completed';
}

function byDate(a, b) {
  return String(a || '').localeCompare(String(b || ''));
}

/** The page's messages per automation run (messages sent outside a run: per customer and appointment). */
function buildGroups(jobs) {
  const map = new Map();
  jobs.forEach((j) => {
    const key = j.run_id ? `run:${j.run_id}` : `${j.customer_id}:${j.kind}:${j.appointment_id || ''}`;
    if (!map.has(key)) {
      map.set(key, {
        key, title: j.automation_name || messageLabel(j), customer_id: j.customer_id, customer_name: j.customer_name,
        customer_phone: j.customer_phone, appointment_id: j.appointment_id, steps: [],
      });
    }
    map.get(key).steps.push(j);
  });
  return [...map.values()]
    .map((g) => {
      const steps = [...g.steps].sort((a, b) => byDate(a.send_at, b.send_at));
      const started = steps.map((s) => s.created_at || s.send_at || '').filter(Boolean).sort()[0] || '';
      return { ...g, steps, status: groupStatus(steps), started };
    })
    .sort((a, b) => byDate(b.started, a.started));
}

function MessageView({ job, onClose }) {
  return (
    <Modal title={messageLabel(job)} onClose={onClose}>
      <div className="de-small de-muted" style={{ marginBottom: 10 }}>
        To {job.customer_name || 'customer'} · {formatPhone(job.customer_phone)} · {formatDateTime(job.send_at)}
        {job.automation_name ? ` · ${job.automation_name}` : ''}
      </div>
      <div className="de-section-label">{job.sent_as === 'text' ? 'Sent as plain text (24-hour window)' : 'WhatsApp template'}</div>
      <div className="de-template-body">{job.sent_as === 'text' ? job.session_body : job.body}</div>
      {job.sent_as !== 'text' && job.session_body && job.session_body !== job.body ? (
        <>
          <div className="de-section-label de-mt-16">Text used if the customer wrote in the last 24 hours</div>
          <div className="de-template-body">{job.session_body}</div>
        </>
      ) : null}
      <div className="de-row de-mt-16" style={{ gap: 8 }}>
        <Badge status={job.status} />
        <span className="de-mono de-muted">{job.template_name}</span>
        <SentAs value={job.sent_as} />
      </div>
      {job.error ? <div className="de-callout de-callout--danger de-mt-16">{job.error}</div> : null}
    </Modal>
  );
}

function ByCustomer({ jobs, reload }) {
  const [expanded, setExpanded] = useState({});
  const [busy, setBusy] = useState(null);
  const groups = useMemo(() => buildGroups(jobs), [jobs]);

  const stopAll = async (g) => {
    const ids = g.steps.filter((s) => s.status === 'pending').map((s) => s.id);
    if (!ids.length) return;
    if (!window.confirm(`Stop ${ids.length} waiting message${ids.length > 1 ? 's' : ''} for ${g.customer_name || g.customer_phone}?`)) return;
    setBusy(g.key);
    try {
      const res = await api.cancelJobs(ids);
      toast.success(`${res.cancelled} message${res.cancelled !== 1 ? 's' : ''} stopped for this customer`);
      await reload();
    } catch (e) {
      toast.error(e.message);
    } finally {
      setBusy(null);
    }
  };

  if (!groups.length) {
    return (
      <div className="de-card">
        <EmptyState icon="🤖" title="No messages found" body="Automations send a message when their trigger happens: a booking, a reply, a service taken …" />
      </div>
    );
  }
  return (
    <div>
      {groups.map((g) => {
        const sent = g.steps.filter((s) => DONE_STATUSES.includes(s.status)).length;
        const pending = g.steps.filter((s) => s.status === 'pending' || s.status === 'scheduled').length;
        const failed = g.steps.filter((s) => s.status === 'failed').length;
        const stoppable = g.steps.some((s) => s.status === 'pending');
        const open = expanded[g.key];
        return (
          <div key={g.key} className="de-card de-flow-card" style={{ borderLeftColor: STATUS_COLOR[g.status] }}>
            <div className="de-row" style={{ alignItems: 'flex-start', gap: 12, flexWrap: 'wrap' }}>
              <div style={{ flex: 1, minWidth: 'min(260px, 100%)' }}>
                <div className="de-row de-row--wrap" style={{ gap: 8, marginBottom: 5 }}>
                  <span className="de-strong" style={{ fontSize: 14 }}>{g.title}</span>
                  <Badge status={g.status} small />
                  <span className="de-pill de-pill--muted de-pill--square">👤 {g.customer_name || 'Customer'}</span>
                  <span className="de-pill de-pill--square de-mono">📱 {formatPhone(g.customer_phone)}</span>
                  {g.appointment_id ? <span className="de-pill de-pill--gold de-pill--square">Appointment #{g.appointment_id}</span> : null}
                </div>
                <div className="de-row de-row--wrap de-small" style={{ gap: 12, color: 'var(--de-muted)' }}>
                  <span>Started {formatDateTime(g.started)}</span>
                  <span style={{ color: STATUS_COLOR.sent }}>✓ {sent} sent</span>
                  {pending ? <span style={{ color: STATUS_COLOR.pending }}>⏳ {pending} to come</span> : null}
                  {failed ? <span style={{ color: STATUS_COLOR.failed }}>✗ {failed} failed</span> : null}
                </div>
              </div>
              <div className="de-row" style={{ gap: 6, flexShrink: 0 }}>
                {stoppable ? (
                  <button type="button" className="de-btn de-btn--danger de-btn--xs" disabled={busy === g.key} onClick={() => stopAll(g)}>
                    {busy === g.key ? <Spinner /> : '🛑'} Stop waiting
                  </button>
                ) : null}
                <button type="button" className="de-btn de-btn--outline de-btn--xs" onClick={() => setExpanded((e) => ({ ...e, [g.key]: !e[g.key] }))}>
                  {open ? '▲ Hide' : '▼ View messages'}
                </button>
              </div>
            </div>
            {open ? (
              <div className="de-table-shell de-mt-16">
                <div className="de-table-wrap">
                  <table className="de-table">
                    <thead>
                      <tr>
                        <th>Message</th>
                        <th>When</th>
                        <th>Status</th>
                        <th>Template</th>
                        <th>Sent as</th>
                      </tr>
                    </thead>
                    <tbody>
                      {g.steps.map((s) => (
                        <tr key={s.id}>
                          <td style={{ maxWidth: 420 }}>
                            <div className="de-strong" style={{ fontSize: 12 }}>{messageLabel(s)}</div>
                            {s.condition ? <div className="de-small de-gold-text">Only if they have not booked again</div> : null}
                            <div className="de-small de-muted" style={{ marginTop: 3, lineHeight: 1.4 }}>{s.body}</div>
                            {s.error ? <div className="de-small" style={{ color: 'var(--de-danger)', marginTop: 3 }}>{s.error}</div> : null}
                          </td>
                          <td className="de-td-mono">{formatDateTime(s.sent_at || s.send_at)}</td>
                          <td><Badge status={s.status} small /></td>
                          <td className="de-td-mono" style={{ color: 'var(--de-purple)' }}>{s.template_name}</td>
                          <td><SentAs value={s.sent_as} /></td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            ) : null}
          </div>
        );
      })}
    </div>
  );
}

function AllMessages({ jobs, reload }) {
  const [view, setView] = useState(null);
  const [busy, setBusy] = useState(null);
  const cancel = async (job) => {
    setBusy(job.id);
    try {
      await api.cancelJob(job.id);
      toast.success('Message cancelled');
      await reload();
    } catch (e) {
      toast.error(e.message);
    } finally {
      setBusy(null);
    }
  };
  if (!jobs.length) return <EmptyState icon="📭" title="No messages" body="Nothing matches these filters." />;
  return (
    <div className="de-table-shell">
      <div className="de-table-wrap">
        <table className="de-table">
          <thead>
            <tr>
              <th>Message</th>
              <th>Automation</th>
              <th>Sent to</th>
              <th>When</th>
              <th>Status</th>
              <th>Sent as</th>
              <th>Error</th>
              <th />
            </tr>
          </thead>
          <tbody>
            {jobs.map((j) => (
              <tr key={j.id}>
                <td className="de-td-strong" style={{ fontSize: 12 }}>
                  {messageLabel(j)}
                  <div className="de-td-mono de-muted">{j.template_name}</div>
                </td>
                <td className="de-small">{j.automation_name || '—'}</td>
                <td>
                  <div>{j.customer_name || '—'}</div>
                  <div className="de-td-mono">{formatPhone(j.customer_phone)}</div>
                </td>
                <td className="de-td-mono">{formatDateTime(j.sent_at || j.send_at)}</td>
                <td><Badge status={j.status} small /></td>
                <td><SentAs value={j.sent_as} /></td>
                <td className="de-small" style={{ maxWidth: 180, color: 'var(--de-danger)', wordBreak: 'break-word' }}>{j.error || '—'}</td>
                <td>
                  <div className="de-row" style={{ gap: 5 }}>
                    {j.status !== 'scheduled' ? (
                      <button type="button" className="de-btn de-btn--outline de-btn--xs" onClick={() => setView(j)}>
                        👁 View
                      </button>
                    ) : null}
                    {j.status === 'pending' ? (
                      <button type="button" className="de-btn de-btn--danger de-btn--xs" disabled={busy === j.id} onClick={() => cancel(j)}>
                        ✕ Cancel
                      </button>
                    ) : null}
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      {view ? <MessageView job={view} onClose={() => setView(null)} /> : null}
    </div>
  );
}

/** Sent and waiting, shown as a tab of Automations. `automations` fills the automation filter. */
export default function ReminderMessages({ automations = [] }) {
  const [view, setView] = useState('customers');
  const [data, setData] = useState(null);
  const [search, setSearch] = useState('');
  const [filter, setFilter] = useState({ q: '', automation_id: '', status: '', offset: 0 });

  useEffect(() => {
    const handle = setTimeout(() => setFilter((f) => (f.q === search.trim() ? f : { ...f, q: search.trim(), offset: 0 })), 350);
    return () => clearTimeout(handle);
  }, [search]);

  const load = () =>
    api
      .listJobsPage({ ...filter, limit: PAGE_SIZE })
      .then(setData)
      .catch((e) => toast.error(e.message || 'Failed to load messages'));

  useEffect(() => {
    setData(null);
    load();
  }, [filter]);

  const set = (patch) => setFilter((f) => ({ ...f, ...patch, offset: 0 }));
  const jobs = data?.items || [];
  const total = data?.total || 0;
  const waiting = jobs.filter((j) => j.status === 'pending' || j.status === 'scheduled').length;
  return (
    <div>
      <div className="de-row" style={{ gap: 10, alignItems: 'center', flexWrap: 'wrap', marginBottom: 12 }}>
        <ChipRow
          chips={[
            { key: 'customers', label: 'By customer' },
            { key: 'history', label: 'All messages' },
          ]}
          value={view}
          onChange={setView}
        />
        <span className="de-small de-muted">
          {SALON_NAME} · {total} message{total === 1 ? '' : 's'}
          {filter.offset === 0 && waiting ? ` · ${waiting} on this page still to send` : ''}
        </span>
      </div>
      <div className="de-card de-filter-bar" style={{ display: 'flex', gap: 12, alignItems: 'flex-end', flexWrap: 'wrap', padding: 18, marginBottom: 14 }}>
        <label className="de-field" style={{ flex: 1, minWidth: 200 }}>
          <span className="de-label--caps">Search by customer</span>
          <input className="de-input de-input--bordered" value={search} onChange={(e) => setSearch(e.target.value)} placeholder="Name or phone — leave empty to see all" />
        </label>
        <label className="de-field" style={{ minWidth: 220 }}>
          <span className="de-label--caps">Automation</span>
          <select className="de-select" value={filter.automation_id} onChange={(e) => set({ automation_id: e.target.value })}>
            <option value="">All automations</option>
            {automations.map((a) => (
              <option key={a.id} value={a.id}>
                {a.name}
              </option>
            ))}
          </select>
        </label>
        <label className="de-field" style={{ minWidth: 190 }}>
          <span className="de-label--caps">Status</span>
          <select className="de-select" value={filter.status} onChange={(e) => set({ status: e.target.value })}>
            <option value="">All messages</option>
            <option value="pending,scheduled">⏳ Waiting to send</option>
            <option value="sent,delivered,read">✅ Sent</option>
            <option value="read">👁️ Read by customer</option>
            <option value="failed">❌ Failed</option>
            <option value="cancelled">🚫 Stopped</option>
          </select>
        </label>
        <button type="button" className="de-btn de-btn--outline" onClick={load}>
          ↻ Refresh
        </button>
      </div>
      {!data ? (
        <Loader />
      ) : view === 'customers' ? (
        <ByCustomer jobs={jobs} reload={load} />
      ) : (
        <AllMessages jobs={jobs} reload={load} />
      )}
      {data && total > PAGE_SIZE ? (
        <div className="de-row" style={{ gap: 10, marginTop: 12, alignItems: 'center' }}>
          <button type="button" className="de-btn de-btn--outline de-btn--sm" disabled={filter.offset === 0} onClick={() => setFilter((f) => ({ ...f, offset: Math.max(0, f.offset - PAGE_SIZE) }))}>
            Previous
          </button>
          <span className="de-small de-muted">
            {filter.offset + 1}–{Math.min(filter.offset + PAGE_SIZE, total)} of {total}
          </span>
          <button type="button" className="de-btn de-btn--outline de-btn--sm" disabled={filter.offset + PAGE_SIZE >= total} onClick={() => setFilter((f) => ({ ...f, offset: f.offset + PAGE_SIZE }))}>
            Next
          </button>
        </div>
      ) : null}
    </div>
  );
}
